import { TypeResolve } from '@libs/constants';

import { IResizeOptions } from './resize-image.interface';


/**------------------------------------------------------
 * Image Resize Helper
 */
export class ResizeImageHelper {

	//** Helper Variables */
	private reader: FileReader = new FileReader();


	/**------------------------------------------------------
	 * Resize Image
	 */
	resizeImageFile(imageFile: File, options: IResizeOptions): Promise<Blob | null> {
		return new Promise(async (resolve: TypeResolve<Blob | null>) => {

			//0 - initialize image element
			const img	 : HTMLImageElement = new Image();
			const blobUrl: string 			= await this.getImageUrl(imageFile);

			//1 - check on image load
			img.onload = async () => {

				//a. resize the image
				const resizedImage: Blob | null = await this.resizeImageHtml(img, options);

				//b. return the result
				URL.revokeObjectURL(blobUrl);
				resolve(resizedImage);
			};

			//2 - return null if error
			img.onerror = () => {
				resolve(null);
			};
			img.src = blobUrl;
		});
	}

	//** Resizes the HTML image element */
	async resizeImageHtml(imageHtml: HTMLImageElement, options: IResizeOptions): Promise<Blob | null> {

		//0 - for maintaining aspect ratio and object-fit: "contain"
		const scale: number = Math.min(options.width / imageHtml.width, options.height / imageHtml.height);
		const drawW: number = imageHtml.width * scale;
		const drawH: number = imageHtml.height * scale;

		//1 - set the image on center
		const dx: number = (options.width - drawW) / 2 + options.padding;
		const dy: number = (options.height - drawH) / 2 + options.padding;

		//2 - create canvas and setting to default width and height
		const canvas: HTMLCanvasElement = document.createElement('canvas');
		canvas.width  = options.width;
		canvas.height = options.height;

		//3 - create context for drawing the image
		const ctx: CanvasRenderingContext2D = canvas.getContext('2d')!;
		ctx.imageSmoothingEnabled = true;
		ctx.imageSmoothingQuality = 'high';

		//4 - clear the canvas and draw the image
		ctx.clearRect(0, 0, options.width, options.height);
		ctx.drawImage(imageHtml, dx, dy, drawW - options.padding * 2, drawH - options.padding * 2);

		//5 - get the blob image from the canvas
		const imgPreview: Blob | null = await this.getBlobFromCanvas(canvas);
		return imgPreview;
	}


	/**------------------------------------------------------
	 * Crop Image
	 */
	// ... make the cropping logic of the research reusable


	/**------------------------------------------------------
	 * Crop Whitespace
	 */
	// ... create based on the old cropping logic


	/**------------------------------------------------------
	 * Download Image
	 */
	imageDimension(blob: Blob): Promise<{ width: number; height: number }> {
		return new Promise((resolve, reject) => {

			//0 - cerate the image
			const img: HTMLImageElement = new Image();
			const url: string = URL.createObjectURL(blob);

			//1 - check on image load
			img.onload = () => {

				//a. get the image dimensions
				const width: number = img.width;
				const height: number = img.height;

				//b. clean up the image
				URL.revokeObjectURL(url); // Clean up the URL
				resolve({ width, height });
			};

			//2 - on image load fail
			img.onerror = (err) => {
				URL.revokeObjectURL(url);
				reject(err);
			};

			//3 - set the image source
			img.src = url;
		});
	}


	/**------------------------------------------------------
	 * Helper Functions
	 */
	private getImageUrl(imageFile: File): Promise<string> {
		return new Promise((resolve: TypeResolve<string>) => {
			this.reader.onload = () => {
				resolve(this.reader.result as string);
			};
			this.reader.readAsDataURL(imageFile);
		});
	}

	private getBlobFromCanvas(canvas: HTMLCanvasElement): Promise<Blob | null> {
		return new Promise((resolve: TypeResolve<Blob | null>) => {
			canvas.toBlob((blob: Blob | null) => { resolve(blob); });
		});
	}
}
